/**
 * EventOutcomePanel – Sprint 2C
 *
 * Zeigt das Ergebnis einer Event-Entscheidung als Panel an.
 * Erscheint nach dem Event-Modal (das Modal schliesst sich zuerst).
 * Schliesst sich nach autoCloseMs automatisch oder bei OK-Klick.
 */

// Typ entspricht packages/shared/src/types/job.types.ts EventOutcome
export interface EventOutcome {
  choiceId: string;
  narrativeText: string;
  rewards: {
    eur: string;
    blackMoney: string;
    xp: number;
    reputationChange: number;
  };
  consequences: {
    wantedLevelChange: number;
    itemFound: string | null;
    contactUnlocked: string | null;
    shiftTimePenalty?: number;
  };
  flavor: 'positive' | 'neutral' | 'negative' | 'mixed';
  isRetroactive: boolean;
}

export interface EventOutcomePanelOptions {
  outcome: EventOutcome;
  onClose: () => void;
  autoCloseMs?: number;
}

// Singleton-Referenz auf das aktive Panel
let activePanel: HTMLElement | null = null;
let activeTimer: ReturnType<typeof setInterval> | null = null;
let activeInterval: ReturnType<typeof setInterval> | null = null;

function clearActiveTimers(): void {
  if (activeTimer !== null) {
    clearTimeout(activeTimer);
    activeTimer = null;
  }
  if (activeInterval !== null) {
    clearInterval(activeInterval);
    activeInterval = null;
  }
}

function flavorHeaderColor(flavor: EventOutcome['flavor']): string {
  switch (flavor) {
    case 'positive': return 'var(--accent-green)';
    case 'negative': return 'var(--accent-red)';
    case 'mixed':    return 'var(--accent-orange)';
    case 'neutral':  return 'var(--text-muted)';
  }
}

function flavorLabel(flavor: EventOutcome['flavor']): string {
  switch (flavor) {
    case 'positive': return 'ERGEBNIS';
    case 'negative': return 'ERGEBNIS';
    case 'mixed':    return 'ERGEBNIS';
    case 'neutral':  return 'ERGEBNIS';
  }
}

function buildRewardLine(outcome: EventOutcome): string {
  const parts: string[] = [];

  const eurVal = parseInt(outcome.rewards.eur, 10);
  if (eurVal !== 0) {
    const absEur = (Math.abs(eurVal) / 100).toFixed(2);
    const color = eurVal > 0 ? 'var(--accent-gold)' : 'var(--accent-red)';
    const prefix = eurVal > 0 ? '+' : '-';
    parts.push(`<div style="font-size:1.05rem;color:${color};letter-spacing:0.04em">${prefix}${absEur}&nbsp;₦</div>`);
  }

  const bmVal = parseInt(outcome.rewards.blackMoney, 10);
  if (bmVal !== 0) {
    const absBm = (Math.abs(bmVal) / 100).toFixed(2);
    const color = bmVal > 0 ? 'var(--accent-bm)' : 'var(--accent-red)';
    const prefix = bmVal > 0 ? '+' : '-';
    parts.push(`<div style="font-size:1.05rem;color:${color};letter-spacing:0.04em">${prefix}${absBm}&nbsp;S-₦</div>`);
  }

  if (outcome.rewards.xp !== 0) {
    const color = outcome.rewards.xp > 0 ? 'var(--accent-gold)' : 'var(--accent-red)';
    const prefix = outcome.rewards.xp > 0 ? '+' : '';
    parts.push(`<div style="color:${color}">${prefix}${outcome.rewards.xp.toString()}&nbsp;XP</div>`);
  }

  if (outcome.rewards.reputationChange !== 0) {
    const color = outcome.rewards.reputationChange > 0 ? 'var(--accent-green)' : 'var(--accent-red)';
    const prefix = outcome.rewards.reputationChange > 0 ? '+' : '';
    parts.push(`<div style="font-size:1rem;color:${color}">RUF&nbsp;${prefix}${outcome.rewards.reputationChange.toString()}</div>`);
  }

  if (outcome.consequences.itemFound !== null) {
    parts.push(`<div style="color:var(--accent-purple)">ITEM: ${outcome.consequences.itemFound}</div>`);
  }

  const wc = outcome.consequences.wantedLevelChange;
  if (wc > 0) {
    parts.push(`<div style="color:var(--accent-red)">FAHNDUNG +${wc.toString()}</div>`);
  } else if (wc < 0) {
    parts.push(`<div style="color:var(--accent-green)">FAHNDUNG ${wc.toString()}</div>`);
  }

  const tp = outcome.consequences.shiftTimePenalty;
  if (tp !== undefined && tp !== 0) {
    const mins = Math.round(Math.abs(tp) / 60);
    const secs = Math.abs(tp) % 60;
    const label = mins > 0 ? `${mins.toString()}min${secs > 0 ? ` ${secs.toString()}s` : ''}` : `${secs.toString()}s`;
    if (tp > 0) {
      parts.push(`<div style="color:var(--accent-red)">SCHICHT +${label}</div>`);
    } else {
      parts.push(`<div style="color:var(--accent-green)">SCHICHT -${label}</div>`);
    }
  }

  return parts.join('');
}

export function showEventOutcomePanel(options: EventOutcomePanelOptions): void {
  const { outcome, onClose, autoCloseMs = 60000 } = options;

  // Bestehendes Panel sofort ersetzen
  hideEventOutcomePanel();

  const headerColor = flavorHeaderColor(outcome.flavor);
  const headerLabel = flavorLabel(outcome.flavor);
  const rewardLine = buildRewardLine(outcome);
  const retroNote = outcome.isRetroactive
    ? '<div class="event-outcome-retro">GESTERN PASSIERT</div>'
    : '';

  const overlay = document.createElement('div');
  overlay.id = 'event-outcome-overlay';
  overlay.className = 'modal-backdrop modal-backdrop--elevated';
  overlay.innerHTML = `
    <div class="event-outcome-panel">
      <div class="event-outcome-header" style="color:${headerColor}">
        <span>${headerLabel}</span>
        <button class="event-outcome-ok-btn" id="event-outcome-ok">OK</button>
      </div>
      ${retroNote}
      <div class="event-outcome-narrative">${outcome.narrativeText}</div>
      <div class="event-outcome-rewards">${rewardLine}</div>
      <div class="event-outcome-countdown">
        <div class="event-outcome-progress-bar">
          <div class="event-outcome-progress-fill" id="event-outcome-fill"></div>
        </div>
        <span class="event-outcome-countdown-label" id="event-outcome-seconds">${Math.round(autoCloseMs / 1000).toString()}s</span>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  activePanel = overlay;

  const fill = overlay.querySelector<HTMLElement>('#event-outcome-fill');
  const secLabel = overlay.querySelector<HTMLElement>('#event-outcome-seconds');

  // Countdown-Balken
  const startTime = Date.now();
  activeInterval = setInterval(() => {
    const elapsed = Date.now() - startTime;
    const ratio = Math.min(elapsed / autoCloseMs, 1);
    const remaining = Math.max(0, Math.ceil((autoCloseMs - elapsed) / 1000));
    if (fill) fill.style.width = `${(ratio * 100).toFixed(1)}%`;
    if (secLabel) secLabel.textContent = `${remaining.toString()}s`;
  }, 100);

  // Auto-Close
  activeTimer = setTimeout(() => {
    clearActiveTimers();
    hideEventOutcomePanel();
    onClose();
  }, autoCloseMs);

  // OK-Button
  const okBtn = overlay.querySelector<HTMLElement>('#event-outcome-ok');
  if (okBtn) {
    okBtn.addEventListener('click', () => {
      clearActiveTimers();
      hideEventOutcomePanel();
      onClose();
    });
  }
}

export function hideEventOutcomePanel(): void {
  clearActiveTimers();
  if (activePanel) {
    activePanel.remove();
    activePanel = null;
  }
}
